import { useState, useRef, useEffect } from 'react';
import { Settings, LogOut, Users, Code2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { getTokenFromStorage } from '@/utils/auth';
import { isAdminJwtToken } from '@/utils/jwtTokenType';
import { getDisplayName, getInitials, getInitialsColor } from '@/utils/userProfileDisplay';

interface UserProfileMenuProps {
  onPageChange?: (page: string) => void;
  /** z-index for the dropdown panel (raise when parent stacking context is elevated, e.g. mobile header). */
  menuZIndexClass?: string;
}

export function UserProfileMenu({ onPageChange, menuZIndexClass = 'z-50' }: UserProfileMenuProps) {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const showDevMode = isAdminJwtToken(getTokenFromStorage());
  const userFallback = t('common.userFallback');
  const displayName = getDisplayName(user, userFallback);
  const brandName = user?.team?.name ?? user?.client?.company_name ?? '';
  const userInitials = getInitials(displayName || userFallback);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const profileMenuRef = useRef<HTMLDivElement>(null);
  const profileBtnRef = useRef<HTMLButtonElement>(null);
  const initialsColor = getInitialsColor(userInitials);

  useEffect(() => {
    if (!showProfileMenu) return;
    const handlePointerDown = (e: PointerEvent) => {
      if (
        profileMenuRef.current && !profileMenuRef.current.contains(e.target as Node) &&
        profileBtnRef.current && !profileBtnRef.current.contains(e.target as Node)
      ) {
        setShowProfileMenu(false);
      }
    };
    document.addEventListener('pointerdown', handlePointerDown);
    return () => document.removeEventListener('pointerdown', handlePointerDown);
  }, [showProfileMenu]);

  return (
    <div className="relative flex items-center gap-2.5">
      <span className="text-[13px] text-[#667085] hidden sm:block" style={{ fontWeight: 500 }}>{brandName}</span>
      <button
        type="button"
        ref={profileBtnRef}
        onClick={() => setShowProfileMenu(!showProfileMenu)}
        className="w-9 h-9 flex items-center justify-center rounded-full transition-all hover:ring-2 hover:ring-gray-200"
        title={displayName || userFallback}
        aria-label={t('nav.accountMenuAria', { name: displayName || userFallback })}
        aria-expanded={showProfileMenu}
        aria-haspopup="menu"
      >
        <div
          className="w-8 h-8 rounded-full flex items-center justify-center select-none"
          style={{ backgroundColor: initialsColor.bg, color: initialsColor.text, fontSize: 12 }}
        >
          <span style={{ fontWeight: 600, letterSpacing: '0.02em' }}>{userInitials}</span>
        </div>
      </button>

      {showProfileMenu && (
        <div
          ref={profileMenuRef}
          className={`absolute top-full right-0 mt-2 w-56 bg-white rounded-xl border border-gray-100 py-1 ${menuZIndexClass}`}
          style={{ boxShadow: '0 8px 30px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.06)' }}
          role="menu"
        >
          <div className="px-4 py-3 border-b border-gray-100">
            <p className="text-sm text-gray-900">{displayName || userFallback}</p>
            {brandName ? <p className="text-xs text-gray-500">{brandName}</p> : null}
          </div>
          <div className="py-1">
            <button
              type="button"
              role="menuitem"
              onClick={() => { setShowProfileMenu(false); onPageChange?.('settings'); }}
              className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
            >
              <Settings className="w-4 h-4 text-gray-400" />
              {t('nav.settings')}
            </button>
            <button
              type="button"
              role="menuitem"
              onClick={() => { setShowProfileMenu(false); onPageChange?.('team'); }}
              className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
            >
              <Users className="w-4 h-4 text-gray-400" />
              {t('nav.team')}
            </button>
            {showDevMode ? (
              <button
                type="button"
                role="menuitem"
                onClick={() => { setShowProfileMenu(false); onPageChange?.('dev-mode'); }}
                className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
              >
                <Code2 className="w-4 h-4 text-gray-400" />
                {t('nav.devMode')}
              </button>
            ) : null}
          </div>
          <div className="border-t border-gray-100 py-1">
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setShowProfileMenu(false);
                logout();
              }}
              className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
            >
              <LogOut className="w-4 h-4 text-gray-400" />
              {t('nav.logOut')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
